import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress, getAccount, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { swapSolForToken, swapTokenForSol } from '../../../utils/orcaSwap';
import { getRelayTokenPriceSolana } from '../../../hooks/useRelayPrice';

interface SolanaTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: {
    address: string; // mint address
    symbol: string;
    name: string;
    logo?: string | null;
    decimals?: number;
    poolAddress?: string; // Orca Whirlpool address
    // Cached pool data (reduces RPC calls)
    tokenVaultA?: string;
    tokenVaultB?: string;
    tickSpacing?: number;
  } | null;
}

type TradeMode = 'buy' | 'sell';

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: #0a0a0a;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    flex-wrap: wrap;
  }
`;

const TokenBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, rgba(153, 69, 255, 0.2) 0%, rgba(20, 241, 149, 0.2) 100%);
  border: 1px solid rgba(153, 69, 255, 0.3);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 13px;
  color: #14F195;
`;

const TokenLogo = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 24px;
    -webkit-tap-highlight-color: transparent;
  }
`;

const WalletSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  
  .wallet-adapter-button {
    background: linear-gradient(135deg, #9945FF 0%, #14F195 100%) !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
  }
`;

const ModeToggle = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 20px;
`;

const ModeButton = styled.button<{ $active: boolean; $mode: TradeMode }>`
  flex: 1;
  padding: 12px 16px;
  background: ${props => props.$active 
    ? props.$mode === 'buy' 
      ? 'linear-gradient(135deg, #14F195 0%, #10b981 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    : 'transparent'};
  border: none;
  border-radius: 8px;
  color: ${props => props.$active ? '#000000' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  -webkit-tap-highlight-color: transparent;
  
  &:hover:not(:disabled) {
    color: ${props => props.$active ? '#000000' : '#ffffff'};
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    min-height: 44px;
    font-size: 15px;
  }
`;

const SwapSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BalanceText = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const MaxButton = styled.button`
  font-size: 11px;
  color: #14F195;
  background: rgba(20, 241, 149, 0.1);
  border: 1px solid rgba(20, 241, 149, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  margin-left: 8px;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: rgba(20, 241, 149, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 4px 10px;
    font-size: 12px;
    min-height: 28px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 16px;
  gap: 12px;
  
  &:focus-within {
    border-color: #9945FF;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    min-height: 56px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    min-height: 52px;
  }
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 24px;
  font-weight: 600;
  outline: none;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
  }
  
  /* Prevent zoom on iOS */
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TokenTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 8px 12px;
`;

const TokenIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;

const TokenSymbol = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const SwapArrow = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
`;

const EstimateRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(20, 241, 149, 0.1);
  border: 1px solid rgba(20, 241, 149, 0.2);
  border-radius: 12px;
`;

const EstimateLabel = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
`;

const EstimateValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #14F195;
`;

const SwapButton = styled.button<{ $loading?: boolean; $mode: TradeMode }>`
  width: 100%;
  padding: 16px;
  background: ${props => props.$loading 
    ? 'rgba(153, 69, 255, 0.3)' 
    : props.$mode === 'buy'
      ? 'linear-gradient(135deg, #14F195 0%, #10b981 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'};
  border: none;
  border-radius: 12px;
  color: ${props => props.$loading ? 'rgba(255, 255, 255, 0.5)' : '#ffffff'};
  font-size: 16px;
  font-weight: 700;
  cursor: ${props => props.$loading ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 8px;
  -webkit-tap-highlight-color: transparent;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.$mode === 'buy' 
      ? '0 8px 24px rgba(20, 241, 149, 0.4)'
      : '0 8px 24px rgba(239, 68, 68, 0.4)'};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 18px;
    font-size: 16px;
    min-height: 52px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    font-size: 15px;
    min-height: 48px;
  }
`;

const LogArea = styled.div`
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px;
  margin-top: 16px;
  max-height: 150px;
  overflow-y: auto;
`;

const LogLine = styled.div<{ $type?: 'success' | 'error' | 'info' }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: ${props => 
    props.$type === 'success' ? '#14F195' : 
    props.$type === 'error' ? '#ef4444' : 
    'rgba(255, 255, 255, 0.7)'};
  margin-bottom: 4px;
  word-break: break-all;
`;

const InfoText = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  margin-top: 16px;
`;

const PoolWarning = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
`;

const WarningText = styled.p`
  color: #ef4444;
  font-size: 14px;
  margin: 0;
`;

const WSOL_MINT_ADDRESS = 'So11111111111111111111111111111111111111112';

const SolanaTradeModal: React.FC<SolanaTradeModalProps> = ({
  isOpen,
  onClose,
  token
}) => {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [mode, setMode] = useState<TradeMode>('buy');
  const [inputAmount, setInputAmount] = useState('0.01');
  const [isSwapping, setIsSwapping] = useState(false);
  const [logs, setLogs] = useState<{ text: string; type: 'success' | 'error' | 'info' }[]>([]);
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [solBalance, setSolBalance] = useState<number>(0);
  const [tokenUsdPrice, setTokenUsdPrice] = useState<number | null>(null);
  const [solUsdPrice, setSolUsdPrice] = useState<number | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);

  // Track if balance fetch is in progress to prevent concurrent calls
  const isFetchingBalancesRef = useRef(false);
  const isFetchingPriceRef = useRef(false);

  // Fetch token + SOL prices for estimation
  useEffect(() => {
    if (!token?.address || !isOpen) return;

    let cancelled = false;

    const fetchPrices = async () => {
      if (isFetchingPriceRef.current) return;
      isFetchingPriceRef.current = true;
      setPriceLoading(true);

      try {
        const [tokenPrice, solPrice] = await Promise.all([
          getRelayTokenPriceSolana(token.address),
          getRelayTokenPriceSolana(WSOL_MINT_ADDRESS)
        ]);

        if (!cancelled) {
          setTokenUsdPrice(tokenPrice);
          setSolUsdPrice(solPrice);
        }
      } catch (error) {
        console.warn('[SolanaTradeModal] Failed to fetch prices:', error);
        if (!cancelled) {
          setTokenUsdPrice(null);
          setSolUsdPrice(null);
        }
      } finally {
        if (!cancelled) {
          setPriceLoading(false);
        }
        isFetchingPriceRef.current = false;
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      isFetchingPriceRef.current = false;
    };
  }, [token?.address, isOpen]);

  // Fetch balances with retry logic for rate limits
  useEffect(() => {
    if (!wallet.publicKey || !token?.address || !isOpen) return;

    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    
    // Reduced retries and better error handling for rate limits
    const fetchWithRetry = async <T,>(fn: () => Promise<T>, retries = 2): Promise<T | null> => {
      for (let i = 0; i < retries; i++) {
        try {
          return await fn();
        } catch (error: any) {
          const errorMessage = error?.message || String(error);
          // If rate limited, don't retry immediately - wait longer
          if (errorMessage.includes('429') || errorMessage.includes('Too Many Requests')) {
            if (i < retries - 1) {
              // Longer backoff for rate limits (5 seconds minimum)
              await sleep(5000 * (i + 1));
            } else {
              console.warn('[SolanaTradeModal] Rate limited, skipping balance fetch');
              return null;
            }
          } else if (i === retries - 1) {
            console.warn('[SolanaTradeModal] Balance fetch failed after retries:', errorMessage);
            return null;
          } else {
            // For other errors, shorter backoff
            await sleep(1000 * (i + 1));
          }
        }
      }
      return null;
    };

    const fetchBalances = async () => {
      // Prevent concurrent balance fetches
      if (isFetchingBalancesRef.current) {
        return;
      }
      isFetchingBalancesRef.current = true;

      try {
        // SOL balance (with retry)
        const solBal = await fetchWithRetry(() => connection.getBalance(wallet.publicKey!));
        if (solBal !== null && typeof solBal === 'number') {
          setSolBalance(solBal / 1e9);
        } else {
          setSolBalance(0);
        }

        // Longer delay between requests to avoid rate limits
        await sleep(500);

        // Token balance (with retry) - use getParsedTokenAccountsByOwner for reliable fetching
        const tokenMint = new PublicKey(token.address);
        
        try {
          // Use getParsedTokenAccountsByOwner to get all token accounts, then find the one we need
          const tokenAccounts = await fetchWithRetry(async () => {
            try {
              return await connection.getParsedTokenAccountsByOwner(wallet.publicKey!, {
                programId: TOKEN_PROGRAM_ID,
              });
            } catch (error) {
              console.warn('[SolanaTradeModal] Error fetching parsed token accounts:', error);
              return null;
            }
          });
          
          if (tokenAccounts && tokenAccounts.value) {
            // Find the token account for this specific token
            const tokenAccount = tokenAccounts.value.find(
              (account: any) => account.account.data.parsed?.info?.mint === token.address
            );
            
            if (tokenAccount) {
              const parsedInfo = tokenAccount.account.data.parsed?.info;
              const tokenAmount = parsedInfo?.tokenAmount;
              
              if (tokenAmount) {
                const balance = tokenAmount.uiAmount || 0;
                const decimals = tokenAmount.decimals || token.decimals || 9;
                setTokenBalance(balance);
                console.log(`[SolanaTradeModal] Token balance: ${balance} (decimals: ${decimals})`);
              } else {
                setTokenBalance(0);
              }
            } else {
              // Token account doesn't exist, balance is 0
              setTokenBalance(0);
              console.log('[SolanaTradeModal] Token account not found, balance is 0');
            }
          } else {
            setTokenBalance(0);
          }
        } catch (error) {
          console.warn('[SolanaTradeModal] Error fetching token balance:', error);
          setTokenBalance(0);
        }
      } catch (error) {
        console.error('[SolanaTradeModal] Error fetching balances:', error);
      } finally {
        isFetchingBalancesRef.current = false;
      }
    };

    fetchBalances();
    // Poll less frequently to avoid rate limits (60 seconds instead of 30)
    const interval = setInterval(fetchBalances, 60000);
    return () => {
      clearInterval(interval);
      isFetchingBalancesRef.current = false;
    };
  }, [wallet.publicKey, token?.address, connection, isOpen]);

  const addLog = (text: string, type: 'success' | 'error' | 'info' = 'info') => {
    setLogs(prev => [...prev, { text, type }]);
  };

  const clearLogs = () => setLogs([]);

  const tokensPerSol = solUsdPrice && tokenUsdPrice && tokenUsdPrice > 0
    ? solUsdPrice / tokenUsdPrice
    : null;

  // Calculate estimated output (based on Relay price API)
  const estimatedOutput = (() => {
    const amount = parseFloat(inputAmount || '0');
    if (!amount || amount <= 0 || !tokensPerSol) return null;
    return mode === 'buy' ? amount * tokensPerSol : amount / tokensPerSol;
  })();

  const estimatedOutputDisplay = (() => {
    if (estimatedOutput === null) {
      return priceLoading ? 'Fetching price...' : '--';
    }
    return mode === 'buy'
      ? estimatedOutput.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : estimatedOutput.toFixed(6);
  })();

  const handleMaxClick = () => {
    if (mode === 'buy') {
      // Leave some SOL for fees
      const maxSol = Math.max(0, solBalance - 0.01);
      setInputAmount(maxSol.toFixed(4));
    } else {
      setInputAmount(tokenBalance.toFixed(0));
    }
  };

  const handleSwap = async () => {
    if (!wallet.publicKey || !wallet.signTransaction || !token?.poolAddress) {
      addLog('❌ Wallet not connected or pool address missing', 'error');
      return;
    }

    const amount = parseFloat(inputAmount);
    if (isNaN(amount) || amount <= 0) {
      addLog(`❌ Please enter a valid ${mode === 'buy' ? 'SOL' : 'token'} amount`, 'error');
      return;
    }

    // Check balance before attempting swap
    if (mode === 'sell') {
      const tokenDecimals = token?.decimals || 9;
      const amountInSmallestUnit = amount * Math.pow(10, tokenDecimals);
      if (tokenBalance < amount) {
        addLog(`❌ Insufficient balance. You have ${tokenBalance.toLocaleString()} ${token?.symbol || 'tokens'}, trying to sell ${amount.toLocaleString()}`, 'error');
        return;
      }
      addLog(`   Balance check: ${tokenBalance.toLocaleString()} ${token?.symbol || 'tokens'} available`, 'info');
    } else {
      if (solBalance < amount) {
        addLog(`❌ Insufficient SOL balance. You have ${solBalance.toFixed(4)} SOL, trying to buy with ${amount} SOL`, 'error');
        return;
      }
      addLog(`   Balance check: ${solBalance.toFixed(4)} SOL available`, 'info');
    }

    setIsSwapping(true);
    clearLogs();

    try {
      // Build cached pool data if available (reduces RPC calls)
      const cachedPoolData = token.tokenVaultA && token.tokenVaultB ? {
        tokenVaultA: token.tokenVaultA,
        tokenVaultB: token.tokenVaultB,
        tickSpacing: token.tickSpacing || 128,
        tokenMintB: token.address,
      } : undefined;
      
      if (cachedPoolData) {
        addLog(`   Using cached pool data (faster!)`, 'info');
      }

      if (mode === 'buy') {
        addLog(`🔄 Buying ${token.symbol} with ${amount} SOL...`, 'info');
        const amountInLamports = BigInt(Math.floor(amount * 1e9));
        
        const result = await swapSolForToken({
          connection,
          wallet: {
            publicKey: wallet.publicKey,
            signTransaction: wallet.signTransaction,
            sendTransaction: wallet.sendTransaction,
          },
          whirlpoolAddress: new PublicKey(token.poolAddress),
          amountIn: amountInLamports,
          minAmountOut: BigInt(0),
          cachedPoolData,
          onLog: (msg) => addLog(msg, 'info'),
        });

        addLog(`✅ Buy successful!`, 'success');
        addLog(`📝 Signature: ${result.signature}`, 'success');
        addLog(`🔗 View on Solscan: https://solscan.io/tx/${result.signature}`, 'info');
      } else {
        addLog(`🔄 Selling ${amount.toLocaleString()} ${token.symbol} for SOL...`, 'info');
        // Use token's actual decimals, default to 9 if not provided
        const tokenDecimals = token.decimals || 9;
        const amountInLamports = BigInt(Math.floor(amount * Math.pow(10, tokenDecimals)));
        
        const result = await swapTokenForSol({
          connection,
          wallet: {
            publicKey: wallet.publicKey,
            signTransaction: wallet.signTransaction,
            sendTransaction: wallet.sendTransaction,
          },
          whirlpoolAddress: new PublicKey(token.poolAddress),
          tokenMint: new PublicKey(token.address),
          amountIn: amountInLamports,
          minAmountOut: BigInt(0),
          cachedPoolData,
          onLog: (msg) => addLog(msg, 'info'),
        });

        addLog(`✅ Sell successful!`, 'success');
        addLog(`📝 Signature: ${result.signature}`, 'success');
        addLog(`🔗 View on Solscan: https://solscan.io/tx/${result.signature}`, 'info');
      }
    } catch (error: any) {
      console.error('Swap error:', error);
      addLog(`❌ Swap failed: ${error.message || error}`, 'error');
    } finally {
      setIsSwapping(false);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setMode('buy');
      setInputAmount('0.01');
      setLogs([]);
    }
  }, [isOpen]);

  if (!token) return null;

  // Detect mobile for responsive modal styling
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Trade Solana Token"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          zIndex: 1000,
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: isMobile ? '0' : '20px',
        },
        content: {
          width: isMobile ? '100%' : '420px',
          maxWidth: isMobile ? '100vw' : '95vw',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? '0' : 'auto',
          borderRadius: isMobile ? '0' : '16px',
          background: '#0a0a0a',
          border: isMobile ? 'none' : '1px solid rgba(153, 69, 255, 0.3)',
          padding: 0,
          inset: isMobile ? '0' : 'auto',
          position: isMobile ? 'fixed' : 'relative',
          overflow: 'auto',
          boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(153, 69, 255, 0.15)',
        },
      }}
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            Trade
            <TokenBadge>
              {token.logo && <TokenLogo src={token.logo} alt={token.symbol} />}
              ${token.symbol}
            </TokenBadge>
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <WalletSection>
          <WalletMultiButton />
        </WalletSection>

        {!token.poolAddress ? (
          <PoolWarning>
            <WarningText>
              ⚠️ No Orca pool found for this token. Trading is not available.
            </WarningText>
          </PoolWarning>
        ) : (
          <SwapSection>
            {/* Buy/Sell Toggle */}
            <ModeToggle>
              <ModeButton 
                $active={mode === 'buy'} 
                $mode="buy"
                onClick={() => {
                  setMode('buy');
                  setInputAmount('0.01');
                }}
              >
                Buy
              </ModeButton>
              <ModeButton 
                $active={mode === 'sell'} 
                $mode="sell"
                onClick={() => {
                  setMode('sell');
                  setInputAmount('1000000');
                }}
              >
                Sell
              </ModeButton>
            </ModeToggle>

            {/* Input */}
            <InputGroup>
              <LabelRow>
                <Label>You Pay</Label>
                <BalanceText>
                  Balance: {mode === 'buy' 
                    ? `${solBalance.toFixed(4)} SOL` 
                    : `${tokenBalance.toLocaleString()} ${token.symbol}`}
                  <MaxButton onClick={handleMaxClick}>MAX</MaxButton>
                </BalanceText>
              </LabelRow>
              <InputWrapper>
                <Input
                  type="number"
                  step={mode === 'buy' ? '0.001' : '1000'}
                  min="0"
                  placeholder={mode === 'buy' ? '0.01' : '1000000'}
                  value={inputAmount}
                  onChange={(e) => setInputAmount(e.target.value)}
                  disabled={isSwapping}
                />
                <TokenTag>
                  {mode === 'buy' ? (
                    <>
                      <TokenIcon src="/landing-assets/Solana.png" alt="SOL" />
                      <TokenSymbol>SOL</TokenSymbol>
                    </>
                  ) : (
                    <>
                      {token.logo && <TokenIcon src={token.logo} alt={token.symbol} />}
                      <TokenSymbol>{token.symbol}</TokenSymbol>
                    </>
                  )}
                </TokenTag>
              </InputWrapper>
            </InputGroup>

            <SwapArrow>↓</SwapArrow>

            {/* Output */}
            <InputGroup>
              <Label>You Receive (Estimated)</Label>
              <InputWrapper>
                <Input
                  type="text"
                  value={estimatedOutputDisplay}
                  readOnly
                  style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                />
                <TokenTag>
                  {mode === 'buy' ? (
                    <>
                      {token.logo && <TokenIcon src={token.logo} alt={token.symbol} />}
                      <TokenSymbol>{token.symbol}</TokenSymbol>
                    </>
                  ) : (
                    <>
                      <TokenIcon src="/landing-assets/Solana.png" alt="SOL" />
                      <TokenSymbol>SOL</TokenSymbol>
                    </>
                  )}
                </TokenTag>
              </InputWrapper>
            </InputGroup>

            <SwapButton
              onClick={handleSwap}
              disabled={!wallet.connected || isSwapping || !token.poolAddress}
              $loading={isSwapping}
              $mode={mode}
            >
              {!wallet.connected 
                ? 'Connect Wallet' 
                : isSwapping 
                  ? 'Swapping...' 
                  : mode === 'buy'
                    ? `Buy ${token.symbol}`
                    : `Sell ${token.symbol}`}
            </SwapButton>

            {logs.length > 0 && (
              <LogArea>
                {logs.map((log, i) => (
                  <LogLine key={i} $type={log.type}>{log.text}</LogLine>
                ))}
              </LogArea>
            )}

            <InfoText>
              Swap directly through Orca Whirlpool • No aggregator fees
            </InfoText>
          </SwapSection>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default SolanaTradeModal;
